import React, { useState, useEffect, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';
import MarkdownRenderer from './MarkdownRenderer';
import AiToolsButton from './AiToolsButton';
import AiReplyGenerator from './AiReplyGenerator';
import ReactionBar from './ReactionBar';

function NexusChat({ initialMessages, isMember, nexusId }) {
    const { t } = useTranslation();
    const { userId, db, collection, serverTimestamp, appId, onSnapshot, query, orderBy, userProfiles, appFunctions, setMessage } = useAppContext();
    const [messages, setMessages] = useState(initialMessages);
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        const q = query(collection(db, `artifacts/${appId}/public/data/nexuses/${nexusId}/chat`), orderBy("timestamp", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsubscribe();
    }, [db, collection, onSnapshot, orderBy, query, appId, nexusId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || isSending) return;

        setIsSending(true);
        const content = newMessage;
        setNewMessage('');

        const sendNexusChatMessage = httpsCallable(appFunctions, 'sendNexusChatMessage');
        try {
            await sendNexusChatMessage({ nexusId, content });
        } catch (error) {
            console.error("Error sending message:", error);
            setNewMessage(content); // Restore message on failure
        } finally {
            setIsSending(false);
        }
    };

    const handleReact = useCallback(async (messageId, emoji, authorId) => {
        try {
            const updateReaction = httpsCallable(appFunctions, 'updateReaction');
            await updateReaction({ entryId: nexusId, commentId: messageId, reaction: emoji, contentType: 'nexusMessage', authorId });
        } catch (error) {
            console.error("Failed to react to nexus message:", error);
            setMessage(`${t('nexusChat.reactionFailed')}: ${error.message}`);
        }
    }, [appFunctions, nexusId, setMessage, t]);


    const handleAiReply = (reply) => {
        setNewMessage(reply);
    };

    if (!isMember) {
        return <div className="text-center text-gray-400 p-8">{t('nexusChat.notMember')}</div>;
    }

    return (
        <div className="flex flex-col h-[60vh] bg-gray-900/50 rounded-lg border border-gray-700/50">
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">
                {messages.map((msg) => {
                    const author = userProfiles.find(p => p.id === msg.from);
                    const isUser = msg.from === userId;
                    const isEmissary = msg.from === 'ai-emissary';
                    const isSystem = msg.from === 'system';

                    if (isSystem) {
                        return (
                            <div key={msg.id} className="text-center text-xs text-red-400 bg-red-900/20 rounded-full py-1.5 px-4 my-2 max-w-md mx-auto">
                                {msg.content}
                            </div>
                        );
                    }

                    return (
                        <div key={msg.id}>
                            <div className={`flex items-end gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
                                {isEmissary ? (
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-500 flex-shrink-0 emissary-avatar">
                                        <LucideIcons.Sparkles size={16} className="text-white" />
                                    </div>
                                ) : (
                                    <img src={author?.photoURL || "https://placehold.co/32x32/AEC6CF/FFFFFF?text=U"} alt={author?.displayName} className="w-8 h-8 rounded-full object-cover" />
                                )}
                                <div className={`max-w-[70%] p-3 rounded-2xl ${isUser ? 'bg-blue-600 text-white rounded-br-md' : isEmissary ? 'bg-purple-900/80 text-gray-200 rounded-bl-md border border-purple-500/50' : 'bg-gray-700 text-gray-100 rounded-bl-md'}`}>
                                    {!isUser && !isEmissary && <p className="text-xs font-bold text-blue-300 mb-1">{author?.displayName}</p>}
                                    {isEmissary && <p className="text-xs font-bold text-purple-300 mb-1">{t('nexusChat.aiEmissary')}</p>}
                                    <p className="text-sm leading-relaxed break-words"><MarkdownRenderer>{msg.content}</MarkdownRenderer></p>
                                </div>
                                <AiToolsButton content={msg.content} contentId={msg.id} contentType="nexusMessage" contextType="nexusMessage" />
                                <AiReplyGenerator content={msg.content} onReplyGenerated={handleAiReply} />
                            </div>
                            {!isSystem && !isEmissary && (
                                <ReactionBar message={msg} onReact={(messageId, emoji) => handleReact(messageId, emoji, msg.from)} isUser={isUser} />
                            )}
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>
            <div className="p-2 border-t border-gray-700/50">
                <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                    <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder={t('nexusChat.placeholder')} className="flex-1 bg-gray-800 rounded-full py-2 px-4 text-white outline-none" />
                    <button type="submit" disabled={isSending} className="p-2 rounded-full text-purple-400 hover:bg-purple-500/20 disabled:opacity-50"><LucideIcons.Send size={20} /></button>
                </form>
                <p className="text-xs text-gray-500 text-center px-2 pt-1">{t('nexusChat.aiHint')} <code className="bg-gray-700 p-0.5 rounded">@Emissary summarize</code> {t('nexusChat.or')} <code className="bg-gray-700 p-0.5 rounded">@Emissary quests</code>.</p>
            </div>
        </div>
    );
}

export default NexusChat;